import type { FormDefinitionDto } from '@hqo/forms-client';
import { Resource } from 'store/resource/types';
import { ResourceParams } from 'shared/types';

/*
 * ⭐ The definition contract comes from the forms team's own published package rather than being
 * hand-copied. ⛔ Do NOT source it from work-orders-admin's mirror types — those are hand-written
 * and have drifted, and work-orders-service carries none at all.
 *
 * ⚠️ Only the DEFINITION types are published. The renderer's CALLBACK types
 * (`FormRendererValidationState`, `IncompleteField`, `Option`, `FileInput`) are internal to
 * forms-admin, which is `"private": true`, so those four are redeclared host-side.
 */
export type FormDefinition = FormDefinitionDto;

/*
 * RES-1050 — the post-booking add-on selection.
 *
 * DELIBERATELY SEPARATE FROM `store/add-ons`, and this is the load-bearing decision in the slice.
 * `store/add-ons` is the PRE-booking selection: five hooks read `selectAllAddOns` and hand the result to
 * `useOnSubmitBookingRequest`, which puts it inside the cart item's `resource_booking.add_ons`. Anything
 * sitting in that slice can therefore be swept into the tenant's next cart.
 *
 * This selection must never reach the cart — it is handed to the Service Request path instead, because the
 * cart resolves exactly one payment recipient (`owner_type` / `owner_id`) and so cannot carry both the
 * landlord's room money and a vendor's.
 */

export interface BookingAddOnSelection {
  /*
   * The catalog item uuid on a native resource. Carried through to the submit payload so Service Request
   * creation can resolve the product. NOT interchangeable with a Prism amenity id, which occupies the same
   * field on a third-party add-on — the write path needs an adapter/provenance guard for that reason.
   */
  id: string;
  name: string;
  /* Minor units. `formatCurrency` divides by 100. */
  price: number;
  /* ISO code. Optional because RES-1049 has not shipped; absent on every Prism/Nexudus add-on. */
  currency?: string;
  description?: string;
  image_url?: string;
  quantity: number;
  required?: boolean;
  /*
   * The catalog company uuid that owns this product. STORED LOWER-CASED HERE.
   * `catalog_company_uuid` collates case-insensitively in MySQL while JS string identity does not, so a raw
   * comparison renders one party as two. RES-1051 re-derives the party server-side and does not trust this
   * value, but RES-1050 acceptance criterion 3 requires it on the payload, so it is belt-and-braces.
   */
  owning_party?: string;
  /*
   * Carried so the totals can tell an unpriceable line from a genuinely free one: a quote-required
   * product arrives as `price: 0` WITH a currency, so summing it would state a firm figure for a job
   * the vendor has not priced. Not sent on the wire -- `buildAddOnSubmission` enumerates its fields.
   */
  pricing_type?: 'fixed' | 'quote_required' | 'free' | 'tiered';
}

export interface BookingAddOnsState {
  submitBookingAddOns: {
    status: string | null;
    error: unknown;
  };
  /*
   * The `resource_booking_details.id` this selection belongs to, as a string.
   *
   * THE POINT OF THIS FIELD: the redux store is session-scoped while this page is booking-scoped, and that
   * mismatch is a real bug if left unguarded. Without it: book room A, add catering, abandon without
   * submitting, book room B — B's step renders pre-selected with A's items and submits them against B's id.
   * `store/add-ons` has four reset call sites and still leaks on the 3DS path; this slice has none, so the
   * guard is structural instead.
   */
  bookingId: string | null;
  addOns: Array<BookingAddOnSelection>;
  /* Lines the submit-time range check refused. Cleared by any change to the selection. */
  quantityViolations: Array<QuantityViolation>;
  /*
   * Definitions already read, keyed by template uuid. Booking-independent by construction, so it
   * survives the slice being claimed for another booking; a null value is a settled "none".
   */
  formDefinitions: Record<string, FormDefinition | null>;
  /*
   * RES-1057. The vendor's form, resolved for whichever party the tenant has chosen.
   *
   * ⚠️ It lives HERE, on the booking-keyed slice, rather than in the questions view's own state,
   * because the slice already claims itself per booking: re-claiming the same id is a no-op, so
   * answers survive the hop between picker and questions, while starting a different booking drops
   * them. A component-local `useState` would lose them on the first unmount and, worse, would carry
   * one booking's answers into the next.
   */
  form: BookingAddOnFormState;
  /*
   * RES-1311. Read on step mount over the WHOLE rendered menu, not over the selection — the stepper
   * has to know a product's minimum BEFORE the tenant adds it, and the form lane's read is keyed on
   * what is already selected.
   *
   * ⛔ A SEPARATE BRANCH FROM `form`, AND THAT IS THE WHOLE DESIGN. Sharing `form`'s action triple
   * would flip `isResolvingForm` and block the CTA on a request that has nothing to do with the form;
   * sharing `pendingKey` is worse, because `isStaleResult` compares against a single key, so whichever
   * lane dispatched last would silently discard the other's success with no error anywhere.
   */
  ranges: BookingAddOnRangesState;
  /*
   * ACCESS-2591. The room re-read with the booking's start, so helix drops add-ons whose minimum notice has
   * passed. ⛔ Kept OUT of the shared resource slot: `ResourcePage` swaps to a skeleton whenever that slot's
   * status is not FULFILLED, which unmounts this step and re-mounts it into another read, forever. The step
   * still re-reads on every mount (notice windows close over time); the key only says which read landed.
   */
  menu: BookingAddOnsMenuState;
  /*
   * Which booking last had an order placed against it from this session, and when. It lives on the
   * store because the add-on surface's route is a sibling of the receipt's, so the booking-details
   * card that reads it is unmounted while the tenant orders. A timestamp rather than a flag: readers
   * measure a bounded window from it, and a second order opens a second window.
   */
  /* The money the card was showing when the tenant left it for the picker. See `markAddOnsAmending`. */
  amendingFrom: { bookingId: string; paymentKey: string | null } | null;
  lastOrdered: { bookingId: string; at: number; paymentKey: string | null } | null;
  /*
   * The same, for a stand-down. Separate from `lastOrdered` because an order is waited on until a
   * payment block exists and a cancel until the money comes back — opposite movements of the same
   * field, and `useOrderedAddOnPaymentQuery` takes the two as separate parameters.
   */
  lastCancelled: { bookingId: string; at: number } | null;
  /*
   * Which booking this slice has already been seeded for, from an order that predates the picker. An
   * explicit flag rather than `addOns.length === 0`: an amend that clears every line leaves the shape
   * an unseeded slice has, and a seed guarded on emptiness would put the order back on next render.
   */
  seededFor: string | null;
}

export interface BookingAddOnsMenuState {
  /* `bookingAddOnsMenuKey(resourceId, startAt)` of the read this state belongs to. */
  key: string | null;
  resource: Resource | null;
  status: string | null;
}

export interface GetBookingAddOnsMenuPayload {
  key: string;
  params: ResourceParams;
}

export interface BookingAddOnFormState {
  /* Resolution status of the owner-forms + definition pair. Fails open: a failure means no form. */
  status: string | null;
  /* Null when no form applies to the chosen party — the common case, since most bookings have none. */
  templateUuid: string | null;
  definition: FormDefinition | null;
  /*
   * The renderer's map, exactly as `onChange` last handed it back — UNNORMALISED.
   * ⛔ Do not strip it here. The strip belongs at the wire edge in `buildFormResponse`, because the
   * renderer re-seeds its own defaults from whatever it is given: normalising on the way in would make
   * the value the tenant sees disagree with the value they typed.
   */
  values: Record<string, unknown>;
  /* From the renderer's `onValidate`. Defaults true so a form-less selection is never gated. */
  isValid: boolean;
  /*
   * What the in-flight resolve was asked for. A success whose `requestKey` no longer matches is
   * discarded. Set to null by the clear, which is what makes an in-flight response unable to
   * repopulate a form the tenant has just emptied.
   */
  pendingKey: string | null;
  /*
   * Catalog item uuids sent to the owner-forms read that came back in NO owner's group. Each has an
   * owner with no instance at this building, which is exactly what the Service Request create rejects
   * on — so surfacing them here turns a failed create into a correctable order.
   */
  unresolvedItemIds: Array<string>;
}

/*
 * THE SUBMIT CONTRACT. One shape, one builder, one call site.
 *
 * ⭐ Enumerated in exactly one place on purpose. RES-1051 records the owning party being dropped
 * independently at every point along the round trip — both read types, both payload constructors, the
 * submit type, cart assembly, helix's DTO and the creation path — because each of those enumerates fields
 * separately. Any one of them was enough to lose it. RES-1185 extends this object with form answers and
 * RES-1057 with the vendor's questions, so it must stay a single seam.
 *
 * ⛔ snake_case, and that is load-bearing rather than cosmetic. The tenant-facing surface is snake_case
 * (`image_url`); the admin Config API is camelCase. helix applies a global `whitelist: true`, which strips
 * any undeclared field SILENTLY — a camelCase guess loses data with no error anywhere.
 */
export interface BookingAddOnSubmission {
  /* The catalog item uuid on a native resource. What Service Request creation resolves the product by. */
  id: string;
  /*
   * The snapshot. Reference AND snapshot are both load-bearing: the reference resolves the vendor and the
   * SR line, the snapshot is the record that survives the vendor editing their catalogue afterwards.
   * helix's shipped AddOnDto declares `name` and `price` @IsNotEmpty(), so a reference-only payload will
   * not validate at all.
   */
  name: string;
  /* Minor units, matching every other price in this app. */
  price: number;
  quantity: number;
  currency?: string;
  description?: string;
  image_url?: string;
  /*
   * Lower-cased. ⚠️ Belt-and-braces, NOT the contract: RES-1051 re-derives the party server-side from the
   * stored catalog rows and explicitly does not trust this value. It is sent because RES-1050 acceptance
   * criterion 3 requires the selection to carry its owning party.
   */
  owning_party?: string;
}

/*
 * A value a tenant can give as an answer, as forms-admin's renderer encodes it. RES-1057.
 *
 * ⛔ NO `null` AND NO `Date`, and both omissions are load-bearing. This mirrors work-orders'
 * `CreateWorkOrderFormFieldValue` rather than forms-service's own `FieldValue`, which DOES admit
 * null — because nothing on this path calls forms-service directly, and the consumer that actually
 * reads these values drops a null without raising anything. An unanswered field is an OMITTED KEY.
 * A date arrives as an ISO-8601 string.
 */
export type FormAnswerValue =
  | string
  | number
  | boolean
  | Array<string>
  | Array<{ fileUuid: string; fileName: string; fileSizeBytes: number }>;

/*
 * The tenant's answers to one vendor's form. RES-1057 produces it; RES-1185 stores it verbatim.
 *
 * ⚠️ The template uuid travels WITH the answers rather than being resolved again at create time.
 * The keys are field KEYS, so a template republished between answering and creating would silently
 * reinterpret them against a different question set.
 */
export interface BookingAddOnFormResponse {
  form_template_uuid: string;
  values: Record<string, FormAnswerValue>;
}

/*
 * One vendor add-on as it was ordered, read back off the booking. Not `BookingAddOnSelection`, which
 * is a menu item being chosen: `price` here is the unit price in minor units with `quantity` beside
 * it, so a line figure is the product of the two. The row carries no currency; the transaction does.
 */
export interface VendorAddOnDto {
  uuid: string;
  name: string;
  quantity: number;
  price: number;
  catalog_item_uuid: string;
  catalog_company_uuid?: string;
  /* Absent when the company resolved no name; one vendor-flagged company in production has none. */
  catalog_company_name?: string;
  /*
   * RES-1316 (helix #11165) — the vendor's logo, for the avatar beside their name. Absent on the
   * same terms as the name: helix drops an empty one rather than emitting it, so this is missing far
   * more often than it is present and the initials disc stays the fallback.
   */
  catalog_company_logo_url?: string;
  /* RES-1377 (helix #11179) — the currency the stored `price` is in. Absent on rows ordered before it existed. */
  currency?: string;
  /*
   * VIS-2193. The product's catalog image and its alt text, for the thumbnail on the booking's own
   * add-ons card. Optional and NOT SENT TODAY: the booking read carries no product image yet, so the
   * card shows the `Package` box until helix adds them, and the image from then on with no change here.
   */
  image_url?: string;
  image_alt_text?: string;
  form_response: BookingAddOnFormResponse | null;
}

/*
 * RES-1057 — work-orders' building-scoped owner-forms read.
 *
 * ⛔ `ownerCompanyUuid` SELECTS the answer rather than confirming it, and it must be the value the
 * CATALOG READ RETURNED, in its original casing. work-orders echoes the selection back through a JS
 * template-literal key built from ITS OWN stored casing — `${catalogItemUuid}:${ownerCompanyUuid}` —
 * and tests it against a set built from ours. The database join is collation-case-insensitive and
 * matches; that JS comparison is not. So a lower-cased uuid drops the item from every group and
 * surfaces as "nobody owns this here" — a false positive shaped exactly like the real failure.
 *
 * ⚠️ The miniapp lower-cases `owning_party` in three places (`toSelection`, `groupAddOnsByParty`,
 * `buildAddOnSubmission`), so the raw value survives ONLY on the catalog `AddOn` off the resource read.
 */
export interface OwnerFormsItem {
  catalogItemUuid: string;
  ownerCompanyUuid: string;
}

export interface GetOwnerFormsParams {
  buildingUuid: string;
  /* 1 to 100. work-orders 422s an empty array. */
  items: Array<OwnerFormsItem>;
}

export interface OwnerFormCatalogItem {
  catalogItemUuid: string;
  /*
   * ⛔ RAW, and the floor is NOT this number. work-orders reports the mapping's own `min_quantity`
   * unchanged, and its create applies `Math.max(1, minQuantity)` — so a zero here means ONE, and a
   * control that offers zero as a valid quantity earns a 400. Derive `effectiveMin` once; never branch
   * on this field directly.
   */
  minQuantity: number;
  /* Null means UNBOUNDED, never a ceiling of zero. */
  maxQuantity: number | null;
}

/*
 * ⚠️ `formUuid: null` DOES NOT MEAN "a form with no questions", and it means two different things
 * depending on the owner. For a VENDOR it is `formTemplateUuids[0] ?? null` with no status filter, so
 * null means no form is attached at all. For the LANDLORD the published filter applies, so null ALSO
 * means "attached but unpublished". A vendor form that IS attached but carries no questions returns a
 * real uuid, and the renderer shows its own empty state.
 */
export interface OwnerForm {
  ownerCompanyUuid: string;
  formUuid: string | null;
  /* The items ECHOED BACK. An item missing from every group has no owner instance at this building. */
  catalogItemUuids: Array<string>;
  /*
   * RES-1311. The same items as `catalogItemUuids`, in the same order, each with the quantity range of
   * the mapping it resolved on. A SIBLING of that array rather than a widening of it, because this app
   * was already consuming it when the field shipped.
   */
  catalogItems: Array<OwnerFormCatalogItem>;
}

/* ⚠️ Wrapped in `data`, not a bare array. */
export interface OwnerFormsApiResponse {
  data: Array<OwnerForm>;
}

/*
 * RES-1311 — the per-product order limits an admin configured on the work-orders type.
 *
 * ⚠️ NOT the tiered-pricing band boundaries, which carry the SAME TWO FIELD NAMES one nesting level
 * down, under a catalog item's prices. Those say which unit price applies at a quantity; these say
 * which quantities the vendor will accept. Wiring the wrong pair bounds the stepper on price breaks
 * and fails quietly, because both are plausible integers. Since RES-1241 the bands DO exist here --
 * `pricing_type` admits `'tiered'` and `price_tiers` carries them -- so the collision is live rather
 * than hypothetical. ⛔ Do not copy this range onto the catalog `AddOn`: keeping it on the
 * owner-forms slice is what keeps the two apart by construction.
 */
export interface AddOnQuantityRange {
  minQuantity: number;
  maxQuantity: number | null;
}

export interface ResolveAddOnRangesPayload {
  buildingUuid: string;
  items: Array<OwnerFormsItem>;
  /* Identity, for the same reason `ResolveAddOnFormPayload` carries it — see the note there. */
  bookingId: string;
  requestKey: string;
}

export interface ResolveAddOnRangesResult {
  ranges: Record<string, AddOnQuantityRange>;
  bookingId: string;
  requestKey: string;
}

export interface BookingAddOnRangesState {
  status: string | null;
  /*
   * Keyed on the catalog item uuid LOWER-CASED. work-orders echoes the uuid back in its own stored
   * casing, and the lookup side is `AddOn.id` from the catalog read — two values that agree in MySQL's
   * collation and not in JS. ⚠️ This is the opposite discipline from `ownerCompanyUuid`, which must be
   * SENT raw: there the casing selects work-orders' answer, here it is only our own map key.
   */
  byCatalogItemUuid: Record<string, AddOnQuantityRange>;
  pendingKey: string | null;
}

export interface GetFormDefinitionParams {
  templateUuid: string;
  lang: string;
}

/* ⚠️ Also wrapped in `data`. */
export interface FormDefinitionApiResponse {
  data: FormDefinition;
}

export interface ResolveAddOnFormPayload {
  buildingUuid: string;
  lang: string;
  items: Array<OwnerFormsItem>;
  /*
   * ⛔ IDENTITY, CARRIED SO A STALE SUCCESS CAN BE REJECTED. Two ways a resolve outlives what asked
   * for it: the tenant clears their selection, which starts NO new request, so `switchMap` never
   * cancels the one in flight; and the slice is claimed for a different booking while a read is still
   * running. In both cases the late result would otherwise be written as if it were current — one
   * booking's vendor questions appearing on another's.
   */
  bookingId: string;
  requestKey: string;
}

export interface ResolveAddOnFormResult {
  templateUuid: string | null;
  definition: FormDefinition | null;
  unresolvedItemIds: Array<string>;
  bookingId: string;
  requestKey: string;
}

/* Labels are localised per fetch, so the language is part of a definition's identity. */
export const formDefinitionKey = (templateUuid: string, lang: string): string => `${templateUuid}@${lang}`;

export interface ReadAddOnFormDefinitionPayload {
  templateUuid: string;
  lang: string;
}

export interface ReadAddOnFormDefinitionResult {
  templateUuid: string;
  lang: string;
  definition: FormDefinition | null;
}

export interface QuantityBounds {
  min: number;
  max: number;
}

export interface QuantityViolation {
  id: string;
  name: string;
  quantity: number;
  min: number;
  max: number;
}

export interface BlockAddOnSubmissionPayload {
  bookingId: string;
  violations: Array<QuantityViolation>;
  ranges: Record<string, AddOnQuantityRange>;
}

export interface AddOnRangeCheck {
  buildingUuid: string;
  items: Array<OwnerFormsItem>;
}

export interface SubmitBookingAddOnsDto {
  ownerType: string;
  ownerUuid: string;
  /* helix resolves either an id or a uuid on this segment. */
  resourceId: string;
  /* `resource_booking_details.id` — what helix's booking routes take as :bookingId. */
  bookingId: string;
  add_ons: Array<BookingAddOnSubmission>;
  rangeCheck?: AddOnRangeCheck;
  /*
   * RES-1057. ⚠️ ABSENT, `null` AND AN OBJECT ARE THREE DIFFERENT INSTRUCTIONS TO helix, not two:
   * absent leaves any stored answers untouched, `null` clears them, an object replaces them. And
   * "untouched" is itself conditional — helix inherits the stored response only when the selection
   * resolves to exactly ONE owning company that already carries one, and CLEARS it otherwise.
   *
   * ⭐ So this client never omits the key. It sends the object when a form applies and `null` when
   * none does, because omission means two different things depending on server state the client
   * cannot see. Optional here only so the type tolerates the shipped callers that predate it.
   *
   * ⛔ A present OBJECT with an empty `add_ons` is a 422 — answers with nothing ordered raise no
   * Service Request. `null` with an empty `add_ons` is the legal way to clear both.
   */
  form_response?: BookingAddOnFormResponse | null;
}
